// ── ParentSnapshotPage ✦ — public, opt-in supportive-visibility view ──
//
// Composition shell. All section bodies live in
// /app/frontend/src/pages/parent/components/* (extracted 2026-05-20
// for maintainability — the constitutional surface deserves small,
// independently testable pieces).
//
// Constitutional rules — see:
//   /app/memory/PARENT_DASHBOARD_PRD.md
//   /app/memory/PARENT_DASHBOARD_CONSTITUTIONAL_CHECKLIST.md
//
// Heuristic during edits: would this make a parent feel calmer,
// or more anxious? Calmer always wins.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Heart } from '@phosphor-icons/react';

import {
  API, BG, PANEL_BORDER, TEXT, TEXT_MUTED, TEXT_SOFT, BLUE, ROSE,
  FONT_STACK, TIER_COLOUR,
} from './components/palette';
import { ParentHero } from './components/ParentHero';
import { UpcomingOpportunitiesSection } from './components/UpcomingOpportunitiesSection';
import { EventJourneySection } from './components/EventJourneySection';
import { SafetyTrustSection } from './components/SafetyTrustSection';
import { WhyEventDropSection } from './components/WhyEventDropSection';
import { DSLRequestForm } from './components/DSLRequestForm';
import { Section } from './components/Section';

const ParentSnapshotPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        // Always call the dashboard endpoint — it works for every
        // active snapshot token (the dashboard IS the snapshot,
        // extended). Defaults to upcoming + journey enabled for
        // backwards compatibility.
        const r = await axios.get(`${API}/parent/dashboard/${token}`);
        setData(r.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This link is no longer active.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return (
      <div
        data-testid="parent-dashboard-loading"
        style={{
          minHeight: '100vh', background: BG, color: TEXT_MUTED,
          fontFamily: FONT_STACK, padding: 40, textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13.5,
        }}
      >
        Loading ✦
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="parent-snapshot-expired"
        style={{
          minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
          padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{ maxWidth: 460, textAlign: 'center' }}>
          <ShieldCheck size={42} weight="fill" color={BLUE} />
          <p style={{ marginTop: 16, fontSize: 20, fontWeight: 700 }}>This link is no longer active</p>
          <p style={{ marginTop: 8, color: TEXT_MUTED, fontSize: 14, lineHeight: 1.6 }}>
            Snapshots stay active for a window of time and can be re-shared by
            the person who sent them. Ask them to send a fresh
            "Share with a parent ✦" link if you'd like to view it again.
          </p>
          <Link
            to="/crew/trust"
            data-testid="parent-snapshot-trust-link"
            style={{
              marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '11px 18px', borderRadius: 999,
              background: `${BLUE}1A`, border: `1px solid ${BLUE}55`,
              color: TEXT, textDecoration: 'none', fontWeight: 700, fontSize: 13,
            }}
          >
            <ShieldCheck size={13} weight="fill" color={BLUE} />
            View Trust Centre ✦
          </Link>
        </div>
      </div>
    );
  }

  const crew = data.crew || {};
  const tierId = crew.tier?.id || 'rising';
  const accent = TIER_COLOUR[tierId] || BLUE;
  const firstName = crew.first_name || 'Crew member';
  const upcoming = data.upcoming || { enabled: true, shifts: [] };
  const journey = data.journey || { enabled: true, completed: [], badges: [], references_count: 0, reference_excerpts: [] };
  const trust = data.trust || {};
  const communication = data.communication || {};
  const since = data.since_last_visit || { enabled: false };

  return (
    <div
      data-testid="parent-dashboard-page"
      data-tier={tierId}
      style={{
        minHeight: '100vh',
        background: `radial-gradient(1200px 600px at 50% -10%, ${accent}14, transparent), ${BG}`,
        color: TEXT, fontFamily: FONT_STACK, paddingBottom: 72,
      }}
    >
      <div style={{ maxWidth: 540, margin: '0 auto', padding: '20px 18px' }}>

        {/* "Shared by" banner — warm, human, brief */}
        <div
          data-testid="parent-dashboard-shared-by"
          style={{
            padding: '12px 16px', borderRadius: 999, marginBottom: 18,
            background: 'rgba(255,255,255,0.035)',
            border: `1px solid ${PANEL_BORDER}`,
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 13, color: TEXT_SOFT,
          }}
        >
          <Heart size={14} weight="fill" color={ROSE} />
          <span>
            Shared by <b style={{ color: TEXT }}>{firstName}</b> ✦
          </span>
        </div>

        <ParentHero crew={crew} since={since} />

        <UpcomingOpportunitiesSection
          firstName={firstName}
          upcoming={upcoming}
          accent={accent}
        />

        <EventJourneySection firstName={firstName} journey={journey} />

        <SafetyTrustSection trust={trust} />

        <Section
          eyebrow="✦ Support"
          title="Need to chat with someone?"
          subtitle="If you have a question for the EventDrop safeguarding team, you're welcome to get in touch."
          testid="parent-dashboard-communication"
          accent={BLUE}
        >
          <DSLRequestForm
            token={token}
            label={communication.dsl_cta_label || 'Request a chat with the Safeguarding Lead ✦'}
          />
        </Section>

        <WhyEventDropSection />

        <p
          data-testid="parent-dashboard-footer"
          style={{
            margin: '24px 0 0', textAlign: 'center',
            color: TEXT_MUTED, fontSize: 11, lineHeight: 1.7,
          }}
        >
          Active until {(data.expires_at || '').slice(0, 10)} ·
          view-only · EventDrop Crew · eventdrop.co
        </p>
      </div>
    </div>
  );
};

export default ParentSnapshotPage;
